/** @jsxImportSource @emotion/react */
import { Approval, Error } from '@mui/icons-material';
import { Grid, IconButton, SvgIcon, Typography, useTheme } from '@mui/material';
import { DataGrid, GridCellParams, GridColDef, GridValueFormatterParams } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';

import { stripedDateGrid } from '../../../common/CustumEmotionStyles';
import { useTranslation } from 'react-i18next';

import { ReactComponent as pendingIcon } from "../../../common/icons/pendingIcon.svg"
import { ReactComponent as pendingReadyIcon } from "../../../common/icons/pendingReadyIcon.svg"
import { DueDateWithColor, remToPixels, StatusIcon } from '../../../common/CustomFunctions';
import DialogDocumentApprover from './DialogDocumentApprover';
import { IApproverDocumentGuiModel } from '../../../model/IApproverGuiModel';
import ApproverBusinessLogic from '../../../businessLogic/ApproverBusinessLogic';

interface IProps {
    org: string,
    status: "ApprovalPending" | "ApprovalPostponed" | null,
    type: "Mine" | "All",
    onlyActive: boolean,
}

function onlyUnique(value: any, index: number, self:any) {
    return self.indexOf(value) === index;
}
function sortDate(a: string, b: string) {
    return new Date(b).getTime() - new Date(a).getTime()
}

const DatagridDocuments = (props: IProps): JSX.Element => {

    const theme = useTheme();
    const [gridRows, setGridRows] = useState<IApproverDocumentGuiModel[]>([]);
    const [gridRowsErrors, setGridRowsErrors] = useState<string[]>([]);

    const [loader, setLoader] = useState(false);

    const [open, setOpen] = useState(false);

    const openDialog = (document: IApproverDocumentGuiModel | undefined) => {
        setApproverDocument(document); //If undefined then approve all available
        setOpen(true);
    }
    const closeDialog = () => {
        setApproverDocument(undefined);
        getDocuments(true);
        setOpen(false);
    }
    const [approverDocument, setApproverDocument] = useState<IApproverDocumentGuiModel | undefined>(undefined)

    const { t, i18n } = useTranslation();

    useEffect(() => {

        let isMounted = true; //Avoid memory leaks. Async function running after dismount.

        getDocuments(isMounted);

        return () => { isMounted = false };

    }, [props.org]);

    function renderStatus(s: number) {
        if (s === 0) {
            return <SvgIcon
                component={pendingIcon}
                color="warning"
                sx={{ fontSize: "1.5rem" }}
                inheritViewBox />
        } else {
            return <SvgIcon
                component={pendingReadyIcon}
                color="primary"
                sx={{ fontSize: "1.5rem" }}
                inheritViewBox />
        }
    }

    const columns: GridColDef[] = [
        {
            field: 'status', headerName: '', width: 60, align: "center",
            renderCell: (params: GridCellParams) => <StatusIcon status={params.value} />
        },
        {field: 'id', headerName: 'id', width: 120,},
        { field: 'assignedUserFullName', headerName: t('basic.assignedTo'), minWidth: 120, flex:1, sortable: false,},
        {field: 'invoiceNo', headerName: t('search.invoiceId'), width: 120, sortable: false,},
        { field: 'supplierName', headerName: t('basic.supplier'), minWidth: 120, flex: 1, sortable: false,},
        { field: 'voucherDate', headerName: t('reports.voucherDate'), width: 120, },
        {
            field: 'dueDate', headerName: t('search.dueDate'), width: 105, sortable: false,
            renderCell: (params: GridCellParams) => <DueDateWithColor params={params} />
        },
        {
            field: 'amount', headerName: t('search.amount'), width: 130, align: "right",
            valueFormatter: (params: GridValueFormatterParams<number>) => {
                if (params.value == null) {
                    return "";
                }
                return new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value);
            }
        },
        {
            field: "edit", headerName: "", width: 60, hide: !props.onlyActive, renderCell: (param: GridCellParams): JSX.Element => {
                return <IconButton onClick={() => openDialog(param.row)}><Approval color="info" /></IconButton>
            }
        },
    ];

    function CustomErrorOverlay() {
        return <Grid container direction="column" justifyContent="center" alignItems="center" height="100%">
            <Grid item><Error fontSize="large"/></Grid>
            <Grid item width="15rem" display="flex" justifyContent="center">
                <Typography variant="body2">
                    {t("basic.unexpectedError")}
                </Typography>
            </Grid>
        </Grid>
    }

    //#######################################################################
    //#######################################################################
    //#######################################################################
    const getDocuments = async (isMounted: boolean) => {
        let logic = new ApproverBusinessLogic();
        setLoader(true);
        const result = await logic.getApproverDocuments(props.org, props.type, props.status);
        setLoader(false);
        if (result.hasErrors) {
            if (isMounted) setGridRowsErrors(result.errors);
        } else {
            if (isMounted) setGridRowsErrors([]);
            if (isMounted) setGridRows(result.value);
        }
    }
    //#######################################################################
    //#######################################################################
    //#######################################################################
    return <><DataGrid css={stripedDateGrid(theme, theme.palette.grey[200])}
        rows={gridRows}
        columns={columns}
        rowHeight={remToPixels(2)}
        loading={loader}
        headerHeight={remToPixels(4)}
        getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        error={gridRowsErrors && gridRowsErrors.length > 0 ? true : undefined}
        components={{
            ErrorOverlay: CustomErrorOverlay,
        }} />
        {(open && approverDocument) && <DialogDocumentApprover
            open={open}
            closeDialog={closeDialog}
            approverDocument={approverDocument}
            approverDocuments={gridRows}
            organizationId={props.org} />}
    </>
}

export default DatagridDocuments;